import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import s from './TextArea.module.scss';
import Label from '../shared/Label';

export type TextAreaOnBlurProps = {
  description?: string;
  disabled?: boolean;
  error?: string;
  hint?: string;
  id?: string;
  label: string;
  name?: string;
  onChange: (value: string, event: ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  optional?: boolean;
  placeholder?: string;
  rows?: number;
  style?: React.CSSProperties;
  value?: string | null;
};

const TextAreaOnBlur: React.FC<TextAreaOnBlurProps> = ({
  description,
  disabled,
  error,
  hint,
  id,
  label,
  name,
  onChange,
  onFocus,
  onBlur,
  optional,
  placeholder,
  rows,
  style,
  value,
}) => {
  const [textValue, setTextValue] = useState(value ?? '');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    setTextValue(value ?? '');
  }, [value]);

  const handleChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>): void => {
    setTextValue(event.target.value);
  }, []);

  const handleOnBlur = useCallback((event: React.FocusEvent<HTMLTextAreaElement>): void => {
    onChangeRef.current(textValue, event);
    onBlur?.();
  }, [textValue, onBlur]);

  return (
    <Label
      label={label}
      required={!optional}
      description={description}
      errorMessage={error}
      hint={hint}
    >
      <textarea
        id={id}
        name={name}
        value={textValue}
        placeholder={placeholder}
        required={!optional}
        onChange={handleChange}
        className={classNames(s['input-field'], s.textarea, !!error && s.error)}
        disabled={disabled}
        rows={rows}
        style={style}
        onFocus={onFocus}
        onBlur={handleOnBlur}
      />
    </Label>
  );
};

export default TextAreaOnBlur;
